/**
 * Persistent Stale-While-Revalidate (SWR) Cache for HTTP GETs.
 *
 * Why this exists
 * ---------------
 * The Dashboard, FlightAgenda, and FlightChecklistPage fire 8-12 HTTP
 * requests on every mount and discard the answers when the user navigates
 * away. Each return trip therefore feels "as if it were the first time".
 *
 * This module persists whitelisted GET responses to IndexedDB (with a
 * graceful localStorage fallback) so that:
 *   1. The next visit paints from cache in <10ms (perceived "instant load").
 *   2. A background refetch keeps the cache warm for the visit after that.
 *   3. Mutations clear matching keys to keep data correct.
 *
 * Security notes
 * --------------
 *   - Cache keys include the active user id + tenant identity so two users
 *     sharing a browser cannot leak data into each other's sessions.
 *   - Cached entries are dropped on logout / token rotation.
 *   - Only safe, non-sensitive read endpoints are whitelisted (see
 *     PERSISTENT_CACHE_RULES in services/api.js).
 *
 * Stale-while-revalidate semantics
 * --------------------------------
 *   - "fresh"  : age < ttlMs               -> use cached, NO network call.
 *   - "stale"  : ttlMs <= age < gcTimeMs   -> use cached + background refresh.
 *   - "expired": age >= gcTimeMs           -> ignore cache + network fetch.
 *
 * Background refreshes update the persistent store silently so the *next*
 * mount gets the fresh data. We deliberately avoid pushing fresh data into
 * the awaiting page mid-render to keep this drop-in compatible with the
 * existing useEffect+axios pattern across the app.
 */

const DEFAULT_GC_TIME_MS = 24 * 60 * 60 * 1000; // 24h ceiling
const DB_NAME = 'aviatto-http-cache';
const DB_VERSION = 1;
const STORE_NAME = 'responses';
const LOCALSTORAGE_PREFIX = 'aviatto:httpCache:';
const SCHEMA_VERSION = 1;
// IndexedDB can occasionally neither succeed nor fail (blocked browser
// storage, corrupted profile state, extension interference). Because every
// cached Dashboard GET consults this module before touching the network, IDB
// must be a bounded optimization, never a load-blocking dependency.
const IDB_OPERATION_TIMEOUT_MS = 750;

let _idbPromise = null;
let _useLocalStorageFallback = false;

function _markIdbUnavailable() {
  _useLocalStorageFallback = true;
}

function _safeStringify(value) {
  try {
    return JSON.stringify(value);
  } catch {
    return null;
  }
}

function _stableSerialize(value) {
  if (value === null || value === undefined) return '';
  if (Array.isArray(value)) {
    return `[${value.map((item) => _stableSerialize(item)).join(',')}]`;
  }
  if (typeof value === 'object') {
    return `{${Object.keys(value)
      .sort()
      .map((key) => `${key}:${_stableSerialize(value[key])}`)
      .join(',')}}`;
  }
  return String(value);
}

/**
 * Build a cache key that is unique per user + endpoint + params so the
 * cached response cannot leak across user sessions.
 */
export function buildCacheKey({ userScope = '', method = 'get', baseURL = '', url = '', params = {} } = {}) {
  const m = String(method || 'get').toLowerCase();
  const u = String(url || '');
  const b = String(baseURL || '');
  const p = _stableSerialize(params || {});
  return `v${SCHEMA_VERSION}|${userScope || 'anon'}|${m}|${b}|${u}|${p}`;
}

function _openIdb() {
  if (_useLocalStorageFallback) return Promise.resolve(null);
  if (_idbPromise) return _idbPromise;

  _idbPromise = new Promise((resolve) => {
    if (typeof indexedDB === 'undefined') {
      _markIdbUnavailable();
      resolve(null);
      return;
    }
    let settled = false;
    let timeoutId = null;
    const finish = (db) => {
      if (settled) return;
      settled = true;
      if (timeoutId) window.clearTimeout(timeoutId);
      resolve(db);
    };
    const fallBack = () => {
      _markIdbUnavailable();
      finish(null);
    };
    let req;
    try {
      req = indexedDB.open(DB_NAME, DB_VERSION);
    } catch {
      fallBack();
      return;
    }
    timeoutId = window.setTimeout(fallBack, IDB_OPERATION_TIMEOUT_MS);
    req.onupgradeneeded = () => {
      try {
        const db = req.result;
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          db.createObjectStore(STORE_NAME);
        }
      } catch {
        // Will surface on subsequent transactions; fallback handled there.
      }
    };
    req.onsuccess = () => finish(req.result);
    req.onerror = fallBack;
    req.onblocked = fallBack;
  });

  return _idbPromise;
}

function _runIdbRequest(action, fallbackValue) {
  return new Promise((resolve) => {
    let settled = false;
    const finish = (value) => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timeoutId);
      resolve(value);
    };
    const timeoutId = window.setTimeout(() => {
      _markIdbUnavailable();
      finish(fallbackValue);
    }, IDB_OPERATION_TIMEOUT_MS);

    try {
      action(finish);
    } catch {
      finish(fallbackValue);
    }
  });
}

async function _idbGet(key) {
  const db = await _openIdb();
  if (!db) return null;
  return _runIdbRequest((finish) => {
    const tx = db.transaction(STORE_NAME, 'readonly');
    const store = tx.objectStore(STORE_NAME);
    const req = store.get(key);
    req.onsuccess = () => finish(req.result || null);
    req.onerror = () => finish(null);
  });
}

async function _idbSet(key, value) {
  const db = await _openIdb();
  if (!db) return false;
  return _runIdbRequest((finish) => {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    const req = store.put(value, key);
    req.onsuccess = () => finish(true);
    req.onerror = () => finish(false);
  });
}

async function _idbDelete(key) {
  const db = await _openIdb();
  if (!db) return false;
  return _runIdbRequest((finish) => {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    const req = store.delete(key);
    req.onsuccess = () => finish(true);
    req.onerror = () => finish(false);
  });
}

async function _idbClear() {
  const db = await _openIdb();
  if (!db) return false;
  return _runIdbRequest((finish) => {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    const req = store.clear();
    req.onsuccess = () => finish(true);
    req.onerror = () => finish(false);
  });
}

async function _idbKeys() {
  const db = await _openIdb();
  if (!db) return [];
  return _runIdbRequest((finish) => {
    const tx = db.transaction(STORE_NAME, 'readonly');
    const store = tx.objectStore(STORE_NAME);
    const req = store.getAllKeys();
    req.onsuccess = () => finish(Array.isArray(req.result) ? req.result : []);
    req.onerror = () => finish([]);
  });
}

function _localStorageGet(key) {
  try {
    const raw = localStorage.getItem(LOCALSTORAGE_PREFIX + key);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function _localStorageSet(key, value) {
  try {
    const serialized = _safeStringify(value);
    if (!serialized) return false;
    // Skip very large entries to avoid quota errors blowing up the app.
    if (serialized.length > 1_000_000) return false;
    localStorage.setItem(LOCALSTORAGE_PREFIX + key, serialized);
    return true;
  } catch {
    return false;
  }
}

function _localStorageDelete(key) {
  try {
    localStorage.removeItem(LOCALSTORAGE_PREFIX + key);
    return true;
  } catch {
    return false;
  }
}

function _localStorageClear() {
  try {
    const toRemove = [];
    for (let i = 0; i < localStorage.length; i += 1) {
      const k = localStorage.key(i);
      if (k && k.startsWith(LOCALSTORAGE_PREFIX)) toRemove.push(k);
    }
    toRemove.forEach((k) => {
      try { localStorage.removeItem(k); } catch { /* noop */ }
    });
    return true;
  } catch {
    return false;
  }
}

function _localStorageKeys() {
  try {
    const keys = [];
    for (let i = 0; i < localStorage.length; i += 1) {
      const k = localStorage.key(i);
      if (k && k.startsWith(LOCALSTORAGE_PREFIX)) {
        keys.push(k.slice(LOCALSTORAGE_PREFIX.length));
      }
    }
    return keys;
  } catch {
    return [];
  }
}

/**
 * Read a persisted entry. Returns the stored envelope or null.
 * Envelope shape: { data, savedAt, ttlMs, gcTimeMs, schema }.
 */
export async function readEntry(key) {
  if (!_useLocalStorageFallback) {
    const fromIdb = await _idbGet(key);
    if (fromIdb) return fromIdb;
  }
  return _localStorageGet(key);
}

/**
 * Persist an entry (best effort; never throws into caller).
 */
export async function writeEntry(key, entry) {
  const envelope = {
    schema: SCHEMA_VERSION,
    savedAt: Date.now(),
    ttlMs: Number(entry?.ttlMs) || 0,
    gcTimeMs: Number(entry?.gcTimeMs) || DEFAULT_GC_TIME_MS,
    data: entry?.data,
  };
  if (!_useLocalStorageFallback) {
    const ok = await _idbSet(key, envelope);
    if (ok) return true;
  }
  return _localStorageSet(key, envelope);
}

/**
 * Delete a single entry.
 */
export async function deleteEntry(key) {
  await _idbDelete(key);
  _localStorageDelete(key);
}

/**
 * Delete every entry whose key matches the predicate.
 */
export async function deleteEntriesMatching(predicate) {
  if (typeof predicate !== 'function') return;
  try {
    const idbKeys = await _idbKeys();
    await Promise.all(
      idbKeys
        .filter((k) => {
          try { return predicate(String(k)); } catch { return false; }
        })
        .map((k) => _idbDelete(k))
    );
  } catch {
    // ignore IDB failures
  }
  _localStorageKeys()
    .filter((k) => {
      try { return predicate(k); } catch { return false; }
    })
    .forEach(_localStorageDelete);
}

/**
 * Drop every persisted entry. Used on logout / token rotation.
 */
export async function clearAll() {
  await _idbClear();
  _localStorageClear();
}

/**
 * Read every persisted entry whose key matches the predicate.
 * Used by the apiClient on boot to hydrate the in-memory sync mirror so
 * pages can read last-known responses without awaiting IndexedDB.
 *
 * Returns an array of `{ key, entry }` tuples (envelope shape unchanged).
 * Best-effort: failures resolve to an empty list rather than throwing.
 */
export async function getAllEntries(predicate) {
  const matchKey = typeof predicate === 'function'
    ? (key) => {
        try { return predicate(String(key)); } catch { return false; }
      }
    : () => true;

  const collected = [];

  try {
    const idbKeys = await _idbKeys();
    const matchingIdbKeys = idbKeys.filter((k) => matchKey(k));
    const idbValues = await Promise.all(matchingIdbKeys.map((k) => _idbGet(k)));
    matchingIdbKeys.forEach((k, idx) => {
      const entry = idbValues[idx];
      if (entry) collected.push({ key: String(k), entry });
    });
  } catch {
    // ignore IDB failures; localStorage may still yield results below.
  }

  try {
    const lsKeys = _localStorageKeys().filter(matchKey);
    lsKeys.forEach((k) => {
      const entry = _localStorageGet(k);
      if (entry) collected.push({ key: k, entry });
    });
  } catch {
    // ignore
  }

  return collected;
}

/**
 * Classify a cached envelope relative to "now".
 *   - 'fresh'   : within ttlMs            -> use directly, no network.
 *   - 'stale'   : within gcTimeMs only    -> use + revalidate in background.
 *   - 'expired' : beyond gcTimeMs         -> ignore.
 */
export function classifyEntry(entry, now = Date.now()) {
  if (!entry || typeof entry !== 'object') return 'expired';
  if (entry.schema !== SCHEMA_VERSION) return 'expired';
  const age = now - Number(entry.savedAt || 0);
  const ttl = Number(entry.ttlMs) || 0;
  const gc = Number(entry.gcTimeMs) || DEFAULT_GC_TIME_MS;
  if (age < ttl) return 'fresh';
  if (age < gc) return 'stale';
  return 'expired';
}

export const __testing__ = {
  _localStorageGet,
  _localStorageSet,
  _localStorageClear,
  resetIdbForTests: () => {
    _idbPromise = null;
    _useLocalStorageFallback = false;
  },
  forceLocalStorageFallback: () => {
    _useLocalStorageFallback = true;
  },
};
